import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { ProductTileLayout } from "@omni/ui/brand/product/productTile/productTile.config";
import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles/tw.merge";
import { useState } from "react";

import { Dropdown } from "@/components/General/Dropdown/dropdown.component";
import { Col } from "@/components/General/Grid/col.component";
import { ColSize } from "@/components/General/Grid/col.variants";
import { Row } from "@/components/General/Grid/row.component";
import { ProductTileRenditions } from "@/components/Product/ProductImage/mappedRenditions";
import { ProductTile } from "@/components/Product/ProductTile/productTile.container";
import WishlistExchangeButton from "@/components/Wishlist/WishlistExchangeButton/wishlistExchangeButton.container";
import { WishlistModel } from "@/redux/modules/api/data/wishlist/@types/wishlist.model";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { TApiAction } from "@/redux/store.types";
import {
    useIsBreakpoint,
    withBreakpoints
} from "@/utils/breakpoints/breakpoints.provider";

import { WishlistTitleRenameModalComponent } from "./WishlistTitleRenameModal/WishlistTitleRenameModal.component";

export type WishlistDetailComponentProps = {
    list: WishlistModel | undefined;
    products: Array<Product<ProductFieldSet.ProductTile>>;
    canRemove?: boolean;
    isLoggedIn: boolean;
    canMove: boolean;
    onRename?: (newName: string) => Promise<TApiAction | undefined>;
    onRemove?: () => void;
    onShare?: () => void;
    onMove?: (sku: string, currentListId: string) => void;
    columnSettings?: {
        default: ColSize;
        sm: ColSize;
        md: ColSize;
        lg: ColSize;
        xl: ColSize;
    };
};

const _WishlistDetailComponent = ({
    list,
    products,
    canRemove,
    isLoggedIn,
    canMove,
    onRename,
    onRemove,
    onShare,
    onMove,
    columnSettings = {
        default: 12,
        sm: 6,
        md: 4,
        lg: 4,
        xl: 3
    }
}: WishlistDetailComponentProps) => {
    const { t } = useGranite();
    const [isRenaming, setRenaming] = useState(false);

    const { matches: isSmall, ready } = useIsBreakpoint("sm");

    const toggleRename = () => {
        setRenaming(true);
    };

    const itemCount = products.length || 0;

    if (!list) return null;

    return (
        <div>
            <div className={cn("tw:flex")}>
                <div className={cn("tw:grow tw:items-center tw:justify-start")}>
                    <h2>
                        {list.name || t("wishlist.wishlistName")}
                        <small
                            className={cn("tw:text-muted-foreground tw:pl-1")}
                        >
                            {t(
                                `wishlist.item.itemcount.${
                                    itemCount === 0
                                        ? "none"
                                        : itemCount > 1
                                          ? "multiple"
                                          : "single"
                                }`,
                                itemCount
                            )}
                        </small>
                    </h2>
                    {isRenaming && (
                        <WishlistTitleRenameModalComponent
                            open={isRenaming}
                            onRename={onRename!}
                            onCancel={() => setRenaming(false)}
                            currentName={
                                list.name || t("wishlist.wishlistName")
                            }
                        />
                    )}
                </div>
                {isLoggedIn && (
                    <div
                        className={cn(
                            "tw:flex tw:md:hidden tw:items-center tw:justify-end"
                        )}
                    >
                        <Dropdown
                            placement={"left-start"}
                            isLabel={false}
                        >
                            <Dropdown.Toggle color={"transparent"}>
                                <Dropdown.Label>
                                    <i
                                        className={cn(
                                            "las la-ellipsis-h la-lg"
                                        )}
                                    />
                                </Dropdown.Label>
                            </Dropdown.Toggle>
                            <Dropdown.Menu className={cn("tw:shadow-lg")}>
                                <Dropdown.Item
                                    onClick={() => {
                                        toggleRename?.();
                                    }}
                                    label={t("wishlist.detail.action.rename")}
                                    className={cn("tw:gap-2 tw:items-center")}
                                >
                                    <i
                                        className={cn(
                                            "tw:block las la-pencil-alt"
                                        )}
                                    ></i>
                                    <span
                                        className={cn(
                                            "tw:block tw:whitespace-nowrap"
                                        )}
                                    >
                                        {t("wishlist.detail.action.rename")}
                                    </span>
                                </Dropdown.Item>
                                {canRemove && (
                                    <Dropdown.Item
                                        onClick={() => {
                                            onRemove?.();
                                        }}
                                        label={t(
                                            "wishlist.detail.action.remove"
                                        )}
                                        className={cn(
                                            "tw:gap-2 tw:items-center"
                                        )}
                                    >
                                        <i
                                            className={cn(
                                                "tw:block las la-trash-alt"
                                            )}
                                        ></i>
                                        <span
                                            className={cn(
                                                "tw:block tw:whitespace-nowrap"
                                            )}
                                        >
                                            {t("wishlist.detail.action.remove")}
                                        </span>
                                    </Dropdown.Item>
                                )}
                                <Dropdown.Item
                                    onClick={() => window.print()}
                                    label={t("wishlist.detail.action.print")}
                                    className={cn("tw:gap-2 tw:items-center")}
                                >
                                    <i
                                        className={cn("tw:block las la-print")}
                                    ></i>
                                    <span
                                        className={cn(
                                            "tw:block tw:whitespace-nowrap"
                                        )}
                                    >
                                        {t("wishlist.detail.action.print")}
                                    </span>
                                </Dropdown.Item>
                                <Dropdown.Item
                                    onClick={() => {
                                        onShare?.();
                                    }}
                                    label={t("wishlist.detail.action.share")}
                                    className={cn("tw:gap-2 tw:items-center")}
                                >
                                    <i
                                        className={cn(
                                            "tw:block las la-share-alt"
                                        )}
                                    ></i>
                                    <span
                                        className={cn(
                                            "tw:block tw:whitespace-nowrap"
                                        )}
                                    >
                                        {t("wishlist.detail.action.share")}
                                    </span>
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                )}
            </div>

            <Row className={cn("tw:pl-4 tw:hidden tw:md:flex")}>
                <Col
                    className={cn("tw:pl-0!")}
                    xs={12}
                >
                    {isLoggedIn && (
                        <Button
                            onClick={toggleRename}
                            type="button"
                            color="link"
                            className={cn("tw:inline-block")}
                        >
                            <i className={cn("las la-pencil-alt", "tw:mr-2")} />
                            {t("wishlist.detail.action.rename")}
                        </Button>
                    )}
                    {canRemove && isLoggedIn && (
                        <Button
                            onClick={onRemove}
                            type="button"
                            color="link"
                            className={cn("tw:inline-block")}
                        >
                            <i className={cn("las la-trash-alt", "tw:mr-2")} />
                            {t("wishlist.detail.action.remove")}
                        </Button>
                    )}
                    <Button
                        onClick={() => window.print()}
                        type="button"
                        color="link"
                        className={cn("tw:inline-block")}
                    >
                        <i className={cn("las la-print", "tw:mr-2")} />
                        {t("wishlist.detail.action.print")}
                    </Button>
                    {isLoggedIn && (
                        <Button
                            onClick={onShare}
                            type="button"
                            color="link"
                            className={cn("tw:inline-block")}
                        >
                            <i className={cn("las la-share-alt", "tw:mr-2")} />
                            {t("wishlist.detail.action.share")}
                        </Button>
                    )}
                </Col>
            </Row>
            <hr className={cn("tw:mb-0")} />
            <Row>
                {products.length === 0 && (
                    <Col xs={12}>
                        <p>{t("wishlist.detail.empty.list")}</p>
                    </Col>
                )}
                {products.length > 0 &&
                    products.map((product, index) => {
                        return (
                            <Col
                                xs={columnSettings.default}
                                sm={columnSettings.sm}
                                md={columnSettings.md}
                                lg={columnSettings.lg}
                                xl={columnSettings.xl}
                                className={cn("tw:mt-4")}
                                key={product.sku}
                            >
                                {ready && (
                                    <ProductTile
                                        className={cn(
                                            "tw:print:grow-0 tw:print:shrink-0 tw:print:basis-[25%] tw:print:max-w-[25%]",
                                            "tw:[&>div]:h-full tw:[&>div>div]:h-full"
                                        )}
                                        sku={product.sku}
                                        product={product}
                                        allowInstantAddToCart={"true"}
                                        extraButtons={() => {
                                            return (
                                                <WishlistExchangeButton
                                                    isLoggedIn={isLoggedIn}
                                                    sku={product.sku}
                                                    currentWishlistId={list.id}
                                                    canMove={canMove}
                                                    onMove={onMove}
                                                />
                                            );
                                        }}
                                        productTileConfig={{
                                            amountOfFeatures: 0,
                                            border: true,
                                            imageRenditions:
                                                ProductTileRenditions,
                                            components: !isSmall
                                                ? ProductTileLayout.WISHLIST_HORIZONTAL()
                                                : ProductTileLayout.WISHLIST(),
                                            ...(!isSmall && {
                                                columnFractions: [5, 7]
                                            })
                                        }}
                                        analyticsContext={{
                                            list: "products-wishlist",
                                            placement: index + 1
                                        }}
                                    />
                                )}
                            </Col>
                        );
                    })}
            </Row>
        </div>
    );
};

export const WishlistDetailComponent = withBreakpoints(
    _WishlistDetailComponent
);
